import { ref, computed, watch, type MaybeRefOrGetter, toValue } from 'vue';
import http from '@/services/http';

/**
 * The real name behind the filename a player reports.
 *
 * A player card only knows what its check-in said, e.g.
 * "0000000000000000000024071003--41819.txt". This turns that into
 * "Inspector Demo Custom List".
 *
 * Asks are deduplicated by name + player and cached module-wide, so the twenty
 * players of a page that share a handful of playlists ask a handful of times,
 * not twenty. Everything queued in one tick is sent together — concurrently,
 * one request per distinct name, not one request for the set. The resolver
 * takes a single name, so a page whose players are all on DIFFERENT lists does
 * cost a request per card; the dedup only pays when lists are shared.
 */

export interface ResolvedPlaylist {
  kind: 'custom_playlist' | 'generated_playlist' | 'unresolved';
  display_name?: string | null;
  name?: string | null;
  source_bucket_id?: number | null;
  source_bucket_name?: string | null;
  location_zone_custom_playlist_id?: number;
  generated_playlist_id?: number;
  location_zone_id?: number;
  file_name?: string | null;
  reported_playlist_name: string;
  location_zone_player_id: number | null;
}

interface PendingAsk {
  name: string;
  location_zone_player_id: number | null;
}

/** name + player is what makes a resolution unique: the same filename in two zones is two playlists. */
function cacheKey(name: string, playerId: number | null): string {
  return `${playerId ?? 'none'}::${name}`;
}

const resolved = ref(new Map<string, ResolvedPlaylist>());
const inFlight = new Set<string>();
let queue: PendingAsk[] = [];
let scheduled = false;

/**
 * One ask against the resolver. A name that resolves to nothing, or a request
 * that fails outright, is not cached — the card keeps showing the raw filename
 * and a later render is free to try again.
 */
async function resolveOne(pendingAsk: PendingAsk): Promise<ResolvedPlaylist | null> {
  try {
    const response = await http.get<ResolvedPlaylist>('/api/playlists/resolve', {
      params: {
        name: pendingAsk.name,
        location_zone_player_id: pendingAsk.location_zone_player_id,
      },
    });
    return {
      ...response.data,
      // Both stamped from the ASK, never from the response. The cache is written
      // and read on the name we requested, so a server that ever normalised the
      // echo (trimming, case-folding, dropping the extension) would otherwise
      // store rows under a key nothing reads — and since `inFlight` is cleared
      // either way, every render would re-request the same name forever.
      reported_playlist_name: pendingAsk.name,
      location_zone_player_id: pendingAsk.location_zone_player_id,
    };
  } catch {
    return null;
  }
}

async function flush(): Promise<void> {
  const batch = queue;
  queue = [];
  scheduled = false;
  if (batch.length === 0) {
    return;
  }

  try {
    const rows = await Promise.all(batch.map(resolveOne));
    const next = new Map(resolved.value);
    for (const row of rows) {
      if (row === null) {
        continue;
      }
      next.set(cacheKey(row.reported_playlist_name, row.location_zone_player_id ?? null), row);
    }
    resolved.value = next;
  } finally {
    // Clearing on failure too, so a later render can retry rather than sitting
    // on a name that never arrives.
    for (const pendingAsk of batch) {
      inFlight.delete(cacheKey(pendingAsk.name, pendingAsk.location_zone_player_id));
    }
  }
}

function ask(name: string, playerId: number | null): void {
  const key = cacheKey(name, playerId);
  if (resolved.value.has(key) || inFlight.has(key)) {
    return;
  }
  inFlight.add(key);
  queue.push({ name, location_zone_player_id: playerId });

  if (!scheduled) {
    scheduled = true;
    // A microtask, so every card mounted in this render joins the same request.
    void Promise.resolve().then(flush);
  }
}

/**
 * The resolved playlist for one reported name, or null until it arrives.
 * Asking is a side effect of reading, so a card just uses the value.
 */
export function useResolvedPlaylistName(
  reportedName: MaybeRefOrGetter<string | null | undefined>,
  locationZonePlayerId: MaybeRefOrGetter<number | null | undefined>,
) {
  function request(): void {
    const name = toValue(reportedName);
    if (typeof name === 'string' && name.trim() !== '') {
      ask(name.trim(), toValue(locationZonePlayerId) ?? null);
    }
  }

  watch(
    () => [toValue(reportedName), toValue(locationZonePlayerId)],
    request,
    { immediate: true },
  );

  const resolution = computed<ResolvedPlaylist | null>(() =>
    readResolvedPlaylist(toValue(reportedName), toValue(locationZonePlayerId) ?? null),
  );

  /**
   * The name to show: the resolved one, falling back to the raw filename.
   * `display_name` carries the bucket when the resolver appends one; `name` is
   * the list's own, and is what a plain custom-playlist resolution returns.
   */
  const displayName = computed<string | null>(() => {
    const name = toValue(reportedName);
    const raw = typeof name === 'string' && name.trim() !== '' ? name.trim() : null;
    return resolution.value?.display_name ?? resolution.value?.name ?? raw;
  });

  return { resolution, displayName };
}

/**
 * The cached resolution for one reported name, or null when none has arrived.
 * It never asks the resolver, so code outside a component can read it. Reading
 * it inside a computed still re-runs that computed when the resolution lands.
 */
export function readResolvedPlaylist(reportedName: string | null | undefined, locationZonePlayerId: number | null): ResolvedPlaylist | null {
  if (typeof reportedName !== 'string' || reportedName.trim() === '') {
    return null;
  }
  return resolved.value.get(cacheKey(reportedName.trim(), locationZonePlayerId)) ?? null;
}

/**
 * The label a card draws for a playing list. The list's own name comes first, because
 * `display_name` already has the bucket appended and the bucket is its own link. With no
 * resolution it is the reported filename. The card and `cardShownMatchFields` both use
 * it, so they agree on whether the label is still the reported name.
 */
export function playlistLabelFor(resolution: ResolvedPlaylist | null, reportedName: string | null): string | null {
  return resolution?.name || resolution?.display_name || reportedName;
}

/** Test seam — the cache is module-level so it survives between components. */
export function __resetResolvedPlaylistNames(): void {
  resolved.value = new Map();
  inFlight.clear();
  queue = [];
  scheduled = false;
}
